import React, { CSSProperties } from 'react';
import { clsx } from 'clsx';
import {
  FloatingArrow,
  FloatingArrowProps,
  FloatingFocusManager,
  FloatingPortal,
  useMergeRefs
} from '@floating-ui/react';

import { color as colorTokens } from '@strava/ui-tokens/js';

import { Button, ButtonProps } from '../Button/Button';
import { PopoverOptions, usePopover } from './usePopover.hook';

import styles from './Popover.module.scss';

/**
 * ============================================
 * ============================================
 * POPOVER CONTEXT
 * ============================================
 * ============================================
 * */
type PopoverContextType =
  | (ReturnType<typeof usePopover> & {
      setLabelId: React.Dispatch<React.SetStateAction<string | undefined>>;
      setDescriptionId: React.Dispatch<
        React.SetStateAction<string | undefined>
      >;
    })
  | null;

const PopoverContext = React.createContext<PopoverContextType>(null);
PopoverContext.displayName = 'PopoverContext';

export const usePopoverContext = () => {
  const context = React.useContext(PopoverContext);

  if (context == null) {
    throw new Error('Popover components must be wrapped in <Popover />');
  }

  return context;
};

export type PopoverProps = {
  children?: React.ReactNode;
  style?: CSSProperties;
  className?: string;
} & Omit<PopoverOptions, 'popoverParentRef'>;

/**
 * ============================================
 * ============================================
 * POPOVER (parent component)
 * ============================================
 * ============================================
 * */
export function Popover({
  children,
  modal = false,
  customRoot = null,
  style,
  className,
  ...restOptions
}: PopoverProps) {
  const popoverParentRef = React.useRef<React.ElementRef<'div'>>(null);
  // This can accept any props as options, e.g. `placement`,
  // or other positioning options.
  const popover = usePopover({
    modal,
    customRoot,
    ...restOptions,
    popoverParentRef
  });

  return (
    <PopoverContext.Provider value={popover}>
      <div
        style={{ width: 'fit-content', ...style }}
        ref={popoverParentRef}
        className={className}
      >
        {children}
      </div>
    </PopoverContext.Provider>
  );
}

/**
 * ============================================
 * ============================================
 * POPOVER TRIGGER BUTTON (compound component)
 * ============================================
 * ============================================
 * */
interface PopoverTriggerProps extends ButtonProps {
  children: React.ReactNode;
  /**
   * asChild = allows the user to pass any element as the anchor.
   * */
  asChild?: boolean;
}

// When `asChild` is true and the user wants to pass their element as the anchor then
// this is used to combine the props of the user's element and
// the popover-related (floating-ui) props (e.g., `data-state` attribute).
const withPopoverProps =
  <PropsType extends object>(
    Component: React.ComponentType<PropsType> | string
  ) =>
  (props: PropsType) => <Component {...props} />;

const PopoverTrigger = React.forwardRef<
  HTMLElement,
  React.HTMLProps<HTMLElement> & PopoverTriggerProps
>(({ children, asChild = false, ...props }, propRef) => {
  const context = usePopoverContext();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const childrenRef = (children as any).ref;
  const ref = useMergeRefs([context.refs.setReference, propRef, childrenRef]);

  // `asChild` allows the user to pass any element as the anchor
  if (asChild && React.isValidElement(children)) {
    const childProps = context.getReferenceProps({
      ref,
      ...props,
      ...children.props,
      'data-state': context.open ? 'open' : 'closed'
    });

    return withPopoverProps(children.type)(childProps);
  }

  return (
    <Button
      ref={ref}
      type="button"
      // The user can style the trigger based on the state
      data-state={context.open ? 'open' : 'closed'}
      {...context.getReferenceProps(props)}
    >
      {children}
    </Button>
  );
});

/**
 * ============================================
 * ============================================
 * POPOVER CONTENT (compound component)
 * ============================================
 * ============================================
 * */

interface PopoverContentProps extends React.HTMLProps<HTMLDivElement> {
  arrowProps?: Omit<FloatingArrowProps, 'ref' | 'context'>;
}

const PopoverContent = React.forwardRef<HTMLDivElement, PopoverContentProps>(
  ({ style, arrowProps, ...props }, propRef) => {
    const { context: floatingContext, ...context } = usePopoverContext();
    const ref = useMergeRefs([context.refs.setFloating, propRef]);
    const isDarkStyling = context.enableDarkStyling;

    if (!floatingContext.open) {
      return null;
    }

    return (
      <FloatingPortal root={context?.customRoot ?? undefined}>
        <FloatingFocusManager context={floatingContext} modal={context.modal}>
          <div
            ref={ref}
            style={{ ...context.floatingStyles, zIndex: 9, ...style }}
            aria-labelledby={context.labelId}
            aria-describedby={context.descriptionId}
            {...context.getFloatingProps(props)}
            className={clsx(
              styles.popover,
              props.className,
              isDarkStyling && styles.darkMode
            )}
          >
            {context.enableArrow && (
              <FloatingArrow
                ref={context.arrowRef}
                context={floatingContext}
                strokeWidth={isDarkStyling ? 1 : 2}
                stroke={
                  isDarkStyling
                    ? colorTokens.colorExtendedNeutralN3
                    : colorTokens.colorCoreN6
                }
                fill={
                  isDarkStyling
                    ? colorTokens.colorExtendedNeutralN1
                    : colorTokens.colorCoreWhite
                }
                {...arrowProps}
              />
            )}
            {props.children}
          </div>
        </FloatingFocusManager>
      </FloatingPortal>
    );
  }
);

/**
 * ============================================
 * ============================================
 * POPOVER CLOSE BUTTON (compound component)
 * ============================================
 * ============================================
 * */
const PopoverClose = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & ButtonProps
>((props, ref) => {
  const { setOpen } = usePopoverContext();
  return (
    <Button
      type="button"
      ref={ref}
      {...props}
      onClick={(event) => {
        props.onClick?.(event);
        setOpen(false);
      }}
    />
  );
});

// Compound components
Popover.Trigger = PopoverTrigger;
Popover.Content = PopoverContent;
Popover.Close = PopoverClose;
